import styles from './admin-change-contract-license-dialog.module.scss';
import { ValueFormatterParams } from 'ag-grid-community';
import { useContext, useState } from 'react';
import { useGetRequestNewHook, useMakeRequestHook } from '../requestUtils';
import {
  CreateHeaders, DatePicker, Dialog, DialogActions, DialogContent, DialogTitle, GrayOutlinedButton, HeaderSizeEnum, Option, PrimaryButton,
  SnackBarContext, TextField, textHeader, VirtualizedSelect
} from '@frontend-workspace/react-shared-lib';
import { IAdminContractLicense } from '../_models/admin-contract-license';
import WebkuaTable from '../webkua-table/webkua-table';
import { useTranslation } from 'react-i18next';

/* eslint-disable-next-line */
export interface AdminChangeContractLicenseDialogProps {
  vismaIdOptions: Option[],
  licenseNumber: number,
  onClose: () => void
}
interface AdminChangeContractLicenseDialogContentProps extends AdminChangeContractLicenseDialogProps {
  license: IAdminContractLicense

}
export function AdminChangeContractLicenseDialog(props: AdminChangeContractLicenseDialogProps) {
  const { data: license }: { data: IAdminContractLicense } = useGetRequestNewHook(`licenses/${props.licenseNumber}`, undefined, [props.licenseNumber]);
  return license ? <AdminChangeContractLicenseDialogContent {...props} license={license} /> : <div></div>;
}

function AdminChangeContractLicenseDialogContent(props: AdminChangeContractLicenseDialogContentProps) {
  const { t } = useTranslation();
  const makeRequest = useMakeRequestHook();
  const { setSnackbarMessage } = useContext(SnackBarContext);
  const [terminationDate, setTerminationDate] = useState(props.license.terminationDate);
  const [parentLicenseNumber, setParentLicenseNumber] = useState<string>(props.license.parentLicenseNumber?.toString() ?? "");

  const handleTerminateDateChange = (newDate: Date | null) => {
    setTerminationDate(newDate?.toISOString() ?? null);

  }
  const handleNewTerminationDate = () => {
    makeRequest(`admin/action/changeLicenseTerminationDate`, 'PUT', {
      licenseNumber: props.license.licenseNumber,
      newTerminationDate: terminationDate
    }).then(result => {
      setSnackbarMessage({ content: t("adminChangeContractLicense.updatedExpirationDate"), type: "success" });
      props.onClose();
    });
  }
  const handleParentLicenseNumberChange = (event: any) => {
    const isNull = parentLicenseNumber === "";
    makeRequest(`admin/action/changeParentLicense`, 'PUT', {
      licenseNumber: props.license.licenseNumber,
      newParentLicenseNumber: isNull ? null : parentLicenseNumber

    }).then(result => {
      setSnackbarMessage({ content: t("adminChangeContractLicense.updatedParentLicenseNumber"), type: "success" });
      props.onClose();
    });
  };

  const vismaIdRenderer = (params: ValueFormatterParams) => {
    const onChange = (vismaId: string) => {
      makeRequest(`admin/action/updateItemInfoOnContractItem`, 'PUT', {
        licenseNumber: props.license.licenseNumber,
        id: params.data.id,
        newVismaId: vismaId
      }).then(result => {
        setSnackbarMessage({ content: t("adminChangeContractLicense.updatedItemType"), type: "success" });
        props.onClose();
      });
    };
    return (
      params.value ?
        <div className={styles['customizedWidth']} >
          <VirtualizedSelect
            options={props.vismaIdOptions}
            placeholder={params.value}
            selectedOption={props.vismaIdOptions.find(x => x.id === params.value)}
            handleSelect={onChange}
          />
        </div> : ""
    );
  };
  const headers = CreateHeaders([
    textHeader("quantity", t("standard.seats"), HeaderSizeEnum.LARGE),
    textHeader("contractItemInfo.id", "Visma ID", HeaderSizeEnum.EXTRA_LARGE, { renderer: vismaIdRenderer }),
    textHeader("terminationDate", t("standard.terminationDate"), HeaderSizeEnum.LARGE),
  ]);

  return <Dialog
    open
    maxWidth="xl"
    fullWidth>
    <DialogTitle className='mb-4'>
      {t("adminChangeContractLicense.changeLicense")}
    </DialogTitle>
    <DialogContent>
      <div>
        <DatePicker
          inputFormat="dd.MM.yyyy"
          value={terminationDate}
          onChange={handleTerminateDateChange}
        />
        <PrimaryButton onClick={handleNewTerminationDate} variant='contained'>
          {t("adminChangeContractLicense.updateExpirationDate")}
        </PrimaryButton>
      </div>
      <TextField
        onBlur={handleParentLicenseNumberChange}
        onChange={(event) => setParentLicenseNumber(event.target.value)}
        value={parentLicenseNumber}
        type="number"
        label={t("standard.parentLicenseNumber")}
      />
      <WebkuaTable
        headers={headers}
        data={props.license.contractItems}
      />
    </DialogContent>
    <DialogActions>
      <GrayOutlinedButton
        variant="outlined"
        size='large'
        onClick={props.onClose}
        className='minButtonWidth'>
        {t("standard.cancel")}
      </GrayOutlinedButton>
    </DialogActions>
  </Dialog>

}

export default AdminChangeContractLicenseDialog;